import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useState } from "react"
import { Pill } from "@/components/ui/badge"
import { CogIcon } from "@/components/ui/icons"
import { workspacesQuery } from "@/lib/queries"
import { cn } from "@/lib/utils"
import { projectsQuery } from "@/lib/workspace-api"
import { MenuSeparator, menuItemClass, Popover } from "./ui/popover"
import { Caret, pickerOrder } from "./workspace-picker"

/**
 * `/ Dictly ▾` inside a project (spec §3.1): this workspace's projects, then Home, Knowledge and "Project
 * settings" (UX pass phase 2), then the
 * workspaces (decision 0056: the only way to switch workspace where the workspace picker is hidden, on a phone and
 * on desktop below 1024px).
 */
export function ProjectSwitcher({
  workspaceId,
  projectId,
  name,
}: {
  workspaceId: string
  projectId: string
  name: string
}) {
  const [open, setOpen] = useState(false)
  const projects = useQuery(projectsQuery(workspaceId)).data ?? []
  const workspaces = useQuery(workspacesQuery).data?.workspaces ?? []
  const listed = projects.filter((p) => p.status !== "archived" || p.id === projectId)
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title="Switch project"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={`Project: ${name}. Switch project`}
          className="tap flex h-8 min-w-0 items-center gap-1 rounded-control px-2 font-semibold text-ink hover:bg-fill"
        >
          <span className="max-w-[16ch] min-w-0 truncate">{name}</span>
          <Caret />
        </button>
      )}
    >
      {listed.map((p) => (
        <Link
          key={p.id}
          role="menuitem"
          to="/w/$workspaceId/p/$projectId"
          params={{ workspaceId, projectId: p.id }}
          onClick={() => setOpen(false)}
          className={cn(menuItemClass, p.id === projectId && "font-semibold")}
        >
          <span className="min-w-0 flex-1 truncate">{p.name}</span>
          {/* decision 0129: the list holds only projects you see; say which are private and which you only read */}
          {p.visibility === "private" && <Pill tone="neutral">private</Pill>}
          {p.access === "reader" && <Pill tone="neutral">not joined</Pill>}
          {p.status !== "active" && <Pill tone="wait">{p.status}</Pill>}
          {p.id === projectId && <span aria-hidden="true">✓</span>}
        </Link>
      ))}
      <MenuSeparator />
      <Link
        role="menuitem"
        to="/w/$workspaceId"
        params={{ workspaceId }}
        onClick={() => setOpen(false)}
        className={menuItemClass}
      >
        Home
      </Link>
      {/* UX pass phase 2 (P14): workspace places one menu away from a project, also where the picker is hidden */}
      <Link
        role="menuitem"
        to="/w/$workspaceId/knowledge"
        params={{ workspaceId }}
        onClick={() => setOpen(false)}
        className={menuItemClass}
      >
        Knowledge
      </Link>
      {/* UX pass 2026-10-15: each switcher holds its own settings (the workspace picker: Workspace settings) */}
      <Link
        role="menuitem"
        to="/w/$workspaceId/p/$projectId/settings"
        params={{ workspaceId, projectId }}
        onClick={() => setOpen(false)}
        className={menuItemClass}
      >
        <CogIcon size={16} className="text-muted" />
        Project settings
      </Link>
      {workspaces.length > 0 && (
        <>
          <MenuSeparator />
          <div className="kicker px-2 pt-1 pb-1">Workspaces</div>
          {pickerOrder(workspaces, workspaceId).map((w) => (
            <Link
              key={w.id}
              role="menuitem"
              to="/w/$workspaceId"
              params={{ workspaceId: w.id }}
              onClick={() => setOpen(false)}
              className={cn(menuItemClass, w.id === workspaceId && "font-semibold")}
            >
              <span className="min-w-0 flex-1 truncate">{w.name}</span>
              {w.id === workspaceId && (
                <>
                  <span aria-hidden="true">✓</span>
                  <span className="sr-only">(current)</span>
                </>
              )}
            </Link>
          ))}
        </>
      )}
    </Popover>
  )
}
